import React from "react";
import {
  Box,
  Button,
  HStack,
  Icon,
  Image,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { IoShareOutline } from "react-icons/io5";
import {
  HEXACO_OFFICIAL_TEST_URL,
  HEXACO_VALID_PDF_GUIDE_IMAGE_URL,
} from "../../constants/hexaco";

interface HexacoInvalidPdfAlertDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenGuide: () => void;
}

interface HexacoRateLimitAlertDialogProps {
  isOpen: boolean;
  message: string;
  onClose: () => void;
}

interface HexacoValidPdfGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HexacoInvalidPdfAlertDialog: React.FC<
  HexacoInvalidPdfAlertDialogProps
> = ({ isOpen, onClose, onOpenGuide }) => {
  const { t } = useTranslation();

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent borderRadius="xl" mx={4}>
        <ModalHeader textAlign="center" pb={2} fontSize="lg">
          {t("hexaco.purchase.invalidPdf.errorTitle")}
        </ModalHeader>
        <ModalBody pt={0} pb={4}>
          <Text fontSize="sm" color="gray.700" textAlign="center" lineHeight="tall">
            {t("hexaco.purchase.invalidPdf.errorMessage")}
          </Text>
        </ModalBody>
        <ModalFooter borderTopWidth="1px" borderColor="gray.200" p={0}>
          <Button
            flex={1}
            variant="ghost"
            colorScheme="teal"
            borderRadius="none"
            borderBottomLeftRadius="xl"
            py={6}
            onClick={onOpenGuide}
          >
            {t("hexaco.purchase.invalidPdf.learnMore")}
          </Button>
          <Box w="1px" bg="gray.200" alignSelf="stretch" />
          <Button
            flex={1}
            variant="ghost"
            colorScheme="teal"
            borderRadius="none"
            borderBottomRightRadius="xl"
            py={6}
            fontWeight="semibold"
            onClick={onClose}
          >
            {t("hexaco.purchase.invalidPdf.ok")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const HexacoRateLimitAlertDialog: React.FC<
  HexacoRateLimitAlertDialogProps
> = ({ isOpen, message, onClose }) => {
  const { t } = useTranslation();

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent borderRadius="xl" mx={4}>
        <ModalHeader textAlign="center" pb={2} fontSize="lg">
          {t("hexaco.purchase.invalidPdf.errorTitle")}
        </ModalHeader>
        <ModalBody pt={0} pb={4}>
          <Text fontSize="sm" color="gray.700" textAlign="center" lineHeight="tall">
            {message}
          </Text>
        </ModalBody>
        <ModalFooter borderTopWidth="1px" borderColor="gray.200" p={0}>
          <Button
            w="full"
            variant="ghost"
            colorScheme="teal"
            borderRadius="none"
            borderBottomRadius="xl"
            py={6}
            fontWeight="semibold"
            onClick={onClose}
          >
            {t("hexaco.purchase.invalidPdf.ok")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

/** Shows desktop + Android + iOS PDF export steps. */
const HexacoPdfGuideInstructions: React.FC = () => {
  const { t } = useTranslation();

  return (
    <VStack align="stretch" spacing={3}>
      <Text fontSize="sm" color="gray.700" lineHeight="tall">
        {t("hexaco.purchase.invalidPdf.guideIntroBefore")}
        {t("hexaco.purchase.invalidPdf.guideIntroLink")}
        <Link
          href={HEXACO_OFFICIAL_TEST_URL}
          isExternal
          display="inline"
          verticalAlign="middle"
          mx={1}
          color="teal.600"
          aria-label={t("hexaco.purchase.invalidPdf.guideIntroLink")}
        >
          <ExternalLinkIcon boxSize="14px" mb="2px" />
        </Link>
        {t("hexaco.purchase.invalidPdf.guideIntroAfter")}
      </Text>

      <Text fontSize="sm" color="gray.600" fontWeight="semibold" pt={1}>
        {t("hexaco.purchase.invalidPdf.guideDesktopTitle")}
      </Text>
      <Text fontSize="sm" color="gray.700" lineHeight="tall">
        • {t("hexaco.purchase.invalidPdf.guideStepPressPrint")}
      </Text>
      <Text fontSize="sm" color="gray.700" lineHeight="tall">
        • {t("hexaco.purchase.invalidPdf.guideStepDestination")}
      </Text>
      <Text fontSize="sm" color="gray.700" lineHeight="tall">
        • {t("hexaco.purchase.invalidPdf.guideStepPages")}
      </Text>
      <Text fontSize="sm" color="gray.700" lineHeight="tall">
        • {t("hexaco.purchase.invalidPdf.guideStepLayout")}
      </Text>

      <Text fontSize="sm" color="gray.600" fontWeight="semibold" pt={1}>
        {t("hexaco.purchase.invalidPdf.guideAndroidTitle")}
      </Text>
      <Text fontSize="sm" color="gray.700" lineHeight="tall">
        • {t("hexaco.purchase.invalidPdf.guideStepHorizontal")}
      </Text>
      <Text fontSize="sm" color="gray.700" lineHeight="tall">
        • {t("hexaco.purchase.invalidPdf.guideStepChangePrinter")}
      </Text>
      <Text fontSize="sm" color="gray.700" lineHeight="tall">
        • {t("hexaco.purchase.invalidPdf.guideStepPressPdfButton")}
      </Text>

      <Text fontSize="sm" color="gray.600" fontWeight="semibold" pt={1}>
        {t("hexaco.purchase.invalidPdf.guideIosTitle")}
      </Text>
      <Text fontSize="sm" color="gray.700" lineHeight="tall">
        • {t("hexaco.purchase.invalidPdf.guideStepHorizontal")}
      </Text>
      <HStack spacing={1} align="center">
        <Text fontSize="sm" color="gray.700" lineHeight="tall">
          • {t("hexaco.purchase.invalidPdf.guideStepShare")}
        </Text>
        <Icon
          as={IoShareOutline as React.ElementType}
          boxSize="16px"
          color="gray.700"
          aria-hidden
        />
      </HStack>
      <Text fontSize="sm" color="gray.700" lineHeight="tall">
        • {t("hexaco.purchase.invalidPdf.guideStepSaveToFiles")}
      </Text>
    </VStack>
  );
};

export const HexacoValidPdfGuideModal: React.FC<
  HexacoValidPdfGuideModalProps
> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  return (
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="lg">
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent borderTopRadius="2xl" borderBottomRadius={{ base: 0, md: "2xl" }} mt={{ base: "auto", md: "3.75rem" }} mb={{ base: 0, md: 16 }} mx={{ base: 0, md: 4 }}>
        <ModalCloseButton />
        <ModalHeader textAlign="center" fontSize="lg" pt={6}>
          {t("hexaco.purchase.invalidPdf.guideTitle")}
        </ModalHeader>
        <ModalBody pb={6}>
          <Box
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="xl"
            p={3}
            mb={4}
            bg="white"
          >
            <Image
              src={HEXACO_VALID_PDF_GUIDE_IMAGE_URL}
              alt={t("hexaco.purchase.invalidPdf.guideTitle")}
              w="full"
              borderRadius="md"
            />
          </Box>

          <Box
            bg="teal.50"
            borderWidth="1px"
            borderColor="teal.100"
            borderRadius="xl"
            px={4}
            py={4}
          >
            <HexacoPdfGuideInstructions />
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
